<script setup lang="ts">
  import type { SupportSession } from '@gat-ship/types'
  import { type SignalRContext, useSignalR } from '@gat-ship/signalr'
  import { useSupportSession } from '@gat-ship/support-session'
  import { globalUtils } from '@gat-ship/utils'
  import { ref } from 'vue'
  import { Button } from '@gat-ship/vue-components'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@gat-ship/vue-components'
  import { useSupportSessionAssigned } from '../../composables/useSupportSessionAssigned'

  // Ends the support session this tab is currently inside. Hosted by
  // SupportSessionHeader (not SupportModalProvider) because it only makes
  // sense in the support-session tab: ending always tears down this tab's
  // SignalR connection + support-session storage and closes the tab after
  // a short countdown. Ending a session from the assigned list goes
  // through SupportSessionModalEndAssigned instead.

  const signalR = useSignalR() as SignalRContext
  const { clearSupportSession } = useSupportSession({
    inject: false
  })
  // Own service instance, not injected: the header lives at the app root,
  // outside the SupportModalProvider instances that the support surfaces
  // mount. The command only fires the end request here — this tab has no
  // assigned list to reconcile.
  const supportSessionAssignedService = useSupportSessionAssigned()

  const supportSession = ref<SupportSession | undefined>(undefined)

  const showEndSupportSessionModal = ref(false)

  const endSupportSessionCountdown = ref<number | undefined>(undefined)

  let endSupportSessionCountdownInterval: number | undefined

  function startEndSupportSessionCountdown(countdownInSeconds: number) {
    endSupportSessionCountdown.value = countdownInSeconds

    endSupportSessionCountdownInterval = window.setInterval(() => {
      endSupportSessionCountdown.value = endSupportSessionCountdown.value! - 1
      if (endSupportSessionCountdown.value === 0) {
        clearInterval(endSupportSessionCountdownInterval)
        endSupportSessionCountdownInterval = undefined
        window.close()
      }
    }, 1000)
  }

  function showModal(params: { supportSession: SupportSession }) {
    supportSession.value = params.supportSession
    showEndSupportSessionModal.value = true
  }

  async function onEndSupportSession() {
    if (supportSession.value?.sessionId) {
      await supportSessionAssignedService.commands.endSupportSession(
        supportSession.value.sessionId,
        globalUtils.functions.uuid()
      )
      signalR.disconnect()
      clearSupportSession()
      startEndSupportSessionCountdown(5)
    }
  }

  defineExpose({
    showModal
  })
</script>

<template>
  <AlertDialog v-model:open="showEndSupportSessionModal">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>End Support Session</AlertDialogTitle>
      </AlertDialogHeader>
      <div class="flex flex-col gap-4 text-sm leading-none">
        <template v-if="endSupportSessionCountdown">
          <span>Support session was ended.</span>
          <span class="text-muted-foreground text-xs">
            Tab will be closing in
            {{ endSupportSessionCountdown }} seconds
          </span>
        </template>
        <template v-else>
          <span>Are you sure you want to end the support session?</span>
          <span>
            This action will end the support session and close the current
            browser tab.
          </span>
          <span>
            You will no longer be able to access this support session.
          </span>
        </template>
      </div>
      <AlertDialogFooter>
        <Button
          v-if="typeof endSupportSessionCountdown !== 'number'"
          variant="outline"
          size="lg"
          @click="onEndSupportSession()"
        >
          End Support Session
        </Button>
        <Button
          variant="ghost"
          size="lg"
          @click="showEndSupportSessionModal = false"
        >
          Cancel
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
